/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Cell, Grid, Heading, HFlow, VFlow } from 'bold-ui'
import { blue, pink } from 'bold-ui/lib/styles/colors'
import { RichTextField } from 'components/form'
import { CboCuidadoCompartilhadoSelectModel } from 'components/form/field/select/CboCuidadoCompartilhadoSelectField/CboCuidadoCompartilhadoSelectField'
import { ReactComponent as CuidadoCompartilhado } from 'images/historico/icone-cuidado-compartilhado.svg'
import { ReactComponent as GuiaEncaminhamento } from 'images/soap/ic_guia-encaminhamento.svg'
import { Fragment, useEffect } from 'react'
import { useField } from 'react-final-form'
import { isUndefinedOrNull } from 'util/checks'
import { MetaRoot } from 'util/metaPath'

import { ClassificacaoPrioridadeField } from '../components/ClassificacaoPrioridadeField'
import { CuidadoCompartilhadoPlanoModel, TipoCompartilhamentoCuidadoPlanoEnum } from '../model-cuidadocompartilhado'
import CuidadoCompartilhadoProblemasCondicoesFields from './CuidadoCompartilhadoProblemasCondicoesFields'
import CuidadoCompartilhadoTICFields from './CuidadoCompartilhadoTICFields'
import CuidadoCompartilhadoUnidadeLotacaoFields from './CuidadoCompartilhadoUnidadeLotacaoFields'
import { TipoCompartilhamentoButton } from './TipoCompartilhamentoButton'

interface CuidadoCompartilhadoFormFieldsPilotoVinculacaoProps {
  path: MetaRoot<CuidadoCompartilhadoPlanoModel>
  isEdit?: boolean
  disabled?: boolean
  richTextFieldRef?: React.RefObject<{ handleReset: () => void }>
  setDisabled?(isDisabled: boolean): void
}

export const CuidadoCompartilhadoFormFieldsPilotoVinculacao = (
  props: CuidadoCompartilhadoFormFieldsPilotoVinculacaoProps
) => {
  const { path, richTextFieldRef, disabled = false, isEdit = false, setDisabled } = props

  const {
    input: { value: cboValue },
  } = useField<CboCuidadoCompartilhadoSelectModel>(path.cbo.absolutePath(), { subscription: { value: true } })

  const { input: registradoViaInput } = useField<TipoCompartilhamentoCuidadoPlanoEnum>(path.tipo.absolutePath(), {
    subscription: { value: true },
  })

  const registradoVia = registradoViaInput.value

  const cboId = cboValue?.cbo?.id
  const cboCodigo2002 = cboValue?.cbo?.cbo2002

  const isGuiaEncaminhamento =
    Boolean(cboValue) && registradoVia === TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO
  const isCuidadoCompartilhado =
    Boolean(cboValue) && registradoVia === TipoCompartilhamentoCuidadoPlanoEnum.CUIDADO_COMPARTILHADO
  const forceDisabledOnEditGuia = isEdit && isGuiaEncaminhamento
  const showCamposCuidado = isCuidadoCompartilhado || forceDisabledOnEditGuia

  const buttomSize = isEdit ? 'small' : 'large'

  useEffect(() => {
    setDisabled?.(isUndefinedOrNull(cboId))
  }, [cboId, setDisabled])

  const discussaoPlaceholder = isGuiaEncaminhamento
    ? 'Insira o motivo do encaminhamento e outras observações'
    : 'Insira objetivamente o relato do caso, além de dúvidas e questionamentos'
  const discussaoMaxLength = isGuiaEncaminhamento ? 2000 : 1000

  const showConcordaTic = isCuidadoCompartilhado || forceDisabledOnEditGuia
  const concordaTicRequired = !disabled && isCuidadoCompartilhado
  const concordaTicDisabled = forceDisabledOnEditGuia || disabled || isGuiaEncaminhamento

  return (
    <Grid>
      <Cell size={12}>
        <VFlow vSpacing={0.5}>
          <Heading level={5}>Registrar via:</Heading>
          <HFlow hSpacing={0.5}>
            <TipoCompartilhamentoButton
              text='Cuidado Compartilhado'
              color={pink}
              backgroundColor={pink.c60}
              colorHoverAtivo={pink.c50}
              icon={<CuidadoCompartilhado />}
              size={buttomSize}
              isAtivo={isCuidadoCompartilhado}
              disabled={disabled || (cboValue?.isGuia && !cboValue.isCuidado)}
              onClick={() => registradoViaInput.onChange(TipoCompartilhamentoCuidadoPlanoEnum.CUIDADO_COMPARTILHADO)}
            />
            <TipoCompartilhamentoButton
              text='Guia de Encaminhamento'
              color={blue}
              backgroundColor={blue.c40}
              colorHoverAtivo={blue.c30}
              icon={<GuiaEncaminhamento />}
              size={buttomSize}
              isAtivo={isGuiaEncaminhamento}
              disabled={disabled}
              onClick={() => registradoViaInput.onChange(TipoCompartilhamentoCuidadoPlanoEnum.GUIA_ENCAMINHAMENTO)}
            />
          </HFlow>
        </VFlow>
      </Cell>

      {(!disabled || isEdit) && (
        <Fragment>
          {showCamposCuidado && (
            <CuidadoCompartilhadoUnidadeLotacaoFields
              path={path}
              cboCodigo2002={cboCodigo2002}
              isGuiaEncaminhamento={isGuiaEncaminhamento}
              isCuidadoCompartilhado={isCuidadoCompartilhado}
              cboId={cboId}
              isEdit={isEdit}
              disabled={disabled}
            />
          )}

          <CuidadoCompartilhadoProblemasCondicoesFields
            path={path}
            isGuiaEncaminhamento={isGuiaEncaminhamento}
            isEdit={isEdit}
            disabled={disabled}
          />

          <Cell size={12}>
            <ClassificacaoPrioridadeField name={path.prioridade} required={!disabled} />
          </Cell>

          <Cell size={12}>
            <RichTextField
              ref={richTextFieldRef}
              name={path.discussao}
              label='Discussão'
              placeholder={discussaoPlaceholder}
              maxLength={discussaoMaxLength}
              required={!disabled}
            />
          </Cell>

          {showConcordaTic && (
            <CuidadoCompartilhadoTICFields path={path} required={concordaTicRequired} disabled={concordaTicDisabled} />
          )}
        </Fragment>
      )}
    </Grid>
  )
}
